import { unref } from 'vue'
import {
    CUSTOM_BLOCK_MAP,
    BLOCK_ACTIONS_MAP,
    BLOCK_TYPES,
} from './components/block-map'
import useWebsocket from '@/api/websocket'
import useApi from '@/views/apps/_customPage/api'
import type { Block, BlockPayload } from '@/views/apps/_customPage/types'
import { type ApiCustomPageComponents, type Container, type CPInputConfiguration, type ExtendedBlock, type ListView, type RepeatableContainer } from 'widget-components'
import { isNotEmpty, isNil, omit, path, uniqBy, isEmpty } from 'ramda'
import useWidgetViewManager from '@/composables/widget-view-manager'
import type { CustomPageLayout, LayoutDescriptor, Message } from '@/types'
import { promiseTimeout } from '@vueuse/core'
import { useI18n } from '@/composables/i18n'
import pako from 'pako'
import type { FixMe } from '@/types/globals'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'
import { updateBlock } from 'widget-components/integrations/CustomPage/blocks-state'

const checkBlockValue = (block: Block) => !isNil(block.currentValue) && block.currentValue !== ''

const getContainerComponentValue = (val: unknown) => {
    if (isNil(val) || val === '') {
        return null
    }
    return val
}

const currentValueEmpty = (block: ExtendedBlock) => {
    return isEmpty(block.currentValue) || isNil(block.currentValue)
}

const transformBlock = (block: ExtendedBlock, fromListView = false, isCp2 = false) => {
    const customTransformBlock = CUSTOM_BLOCK_MAP[block.type]

    if (isCp2) {
        const isEmpty = customTransformBlock?.currentValueEmpty?.(block) ?? currentValueEmpty(block)

        if (isEmpty) return undefined
    }

    if (customTransformBlock?.transform) {
        return customTransformBlock.transform(block)
    }

    const { id, type, currentValue } = block

    return {
        id,
        type,
        // backend wants null for empty values when block is from LIST_VIEW
        value: fromListView
            ? getContainerComponentValue(currentValue)
            : currentValue,
    }
}

export const checkIfComponentIsFormComponent = (block?: ExtendedBlock) => {
    return block?.internalData?.type === BLOCK_TYPES.FORM
}

export const mergeListViewComponentCurrentValue = (
    container: ExtendedBlock<ListView>,
    containerItemIndex: number,
    containerItemComponents: ExtendedBlock<ApiCustomPageComponents>[],
) => {
    return containerItemComponents.map((c, index) => {
        return {
            ...c,
            currentValue: container.currentValue?.[containerItemIndex]?.[index],
        }
    })
}

const transformListOfComponents = async (componentList: ExtendedBlock[], fromListView = false, isCp2 = false) => {
    const componentValues = []
    const trackers = []
    for (const block of componentList) {
        // send empty values for container components
        if (fromListView || checkBlockValue(block)) {
            const transformedBlockValue = await transformBlock(block, fromListView, isCp2)
            if (transformedBlockValue) {
                componentValues.push(transformedBlockValue)
            }

            const addedBlock = componentValues[componentValues.length - 1]
            const blockTrackerAction: FixMe = BLOCK_ACTIONS_MAP[block.type as keyof typeof BLOCK_ACTIONS_MAP]

            if (blockTrackerAction) {
                if (typeof blockTrackerAction?.transform === 'function') {
                    trackers.push(...blockTrackerAction.transform(block, addedBlock))
                } else {
                    trackers.push({
                        actionName: blockTrackerAction.actionName,
                        props: {
                            fieldName: (block as ExtendedBlock<CPInputConfiguration>).fieldName,
                            fieldValue: block.currentValue,
                        },
                    })
                }
            }
        }
    }

    return {
        componentValues,
        trackers,
    }
}

async function getContainerChildrenPayload(container: ExtendedBlock<Container | RepeatableContainer>, values: FixMe[] = [], trackers: FixMe[] = [], fromListView: boolean, isCp2 = false) {
    if (('children' in container || {}) && Array.isArray((container as ExtendedBlock<Container>).children)) {
        for (const child of container.children) {
            if ('children' in child) {
                const { values: childValues, trackers: childTrackers } = await getContainerChildrenPayload(child as unknown as ExtendedBlock<Container>, [], [], fromListView, isCp2)
                values.push(...childValues)
                trackers.push(...childTrackers)
            } else if (Array.isArray(child)) {
                const result = await Promise.all(
                    child.map(c => getContainerChildrenPayload(c as unknown as ExtendedBlock<Container>, [], [], fromListView, isCp2)),
                )
                values.push(...result.flatMap(r => r.values))
                trackers.push(...result.flatMap(r => r.trackers))
            } else {
                const { componentValues, trackers: componentTrackers } = await transformListOfComponents([child], fromListView, isCp2)
                values.push(...componentValues)
                trackers.push(...componentTrackers)
            }
        }
        return { values, trackers }
    }

    const component = container as ExtendedBlock

    const { componentValues, trackers: componentTrackers } = await transformListOfComponents([component], fromListView, isCp2)
    values.push(...componentValues)
    trackers.push(...componentTrackers)

    return { values, trackers }
}

export const clearBlockErrors = (blocks: ExtendedBlock[]) => {
    return flattenBlocks(blocks).forEach(b => {
        if (b.error) {
            updateBlock({
                ...b,
                error: undefined,
            })
        }
    })
}

export const prepareV1Payload = async (
    blocks: ExtendedBlock[],
    action?: FixMe,
    fromListView = false,
) => {
    const notContainerComponents = unref(blocks)
        .filter(block => checkIfComponentIsFormComponent(block) && block.type !== 'LIST_VIEW')

    const containerComponents = unref(blocks)
        .filter(block => checkIfComponentIsFormComponent(block) && block.type === 'LIST_VIEW')

    const { componentValues, trackers } = await transformListOfComponents(notContainerComponents, fromListView, false)

    if (action) componentValues.push(action)

    const containerValues: FixMe[] = []

    for (const container of containerComponents as ExtendedBlock<ListView>[]) {
        if ('items' in container && container.items) {
            const transformedContainer = await transformBlock(container, fromListView)
            if (!transformedContainer) continue

            for (const containerItemIndex in container.items) {
                const containerItem = container.items[containerItemIndex]
                const components = mergeListViewComponentCurrentValue(
                    container,
                    Number(containerItemIndex),
          containerItem.components as ExtendedBlock<ApiCustomPageComponents>[],
                )

                const { componentValues: containerComponentValues } = await prepareV1Payload(components, undefined, true)

                transformedContainer.items.push({
                    id: containerItem.index,
                    values: containerComponentValues,
                })
            }

            if (transformedContainer.items.some((i: { values: unknown[] }) => i.values.length)) {
                containerValues.push(transformedContainer)
            }
        }
    }

    return { trackers, componentValues, containerValues }
}

export const prepareV2Payload = async (
    blocks: ExtendedBlock[],
    action?: FixMe,
) => {
    const containerTypes = ['CONTAINER', 'REPEATABLE_CONTAINER', 'TABBED_CONTAINER']

    const notContainerComponents = unref(blocks)
        .filter(block => checkIfComponentIsFormComponent(block) && !containerTypes.includes(block.type))

    const containerComponents = unref(blocks)
        .filter(block => checkIfComponentIsFormComponent(block) && containerTypes.includes(block.type))

    const { componentValues, trackers } = await transformListOfComponents(notContainerComponents, false, true)

    if (action) componentValues.push(action)

    for (const container of containerComponents as ExtendedBlock<Container>[]) {
        if ('children' in container && container.children) {
            const { values, trackers: containerTrackers } = await getContainerChildrenPayload(container, [], [], false, true)
            componentValues.push(...values)
            trackers.push(...containerTrackers)
        }
    }

    return {
        trackers,
        componentValues: componentValues.filter(c => {
            const val = Object.values(omit(['id', 'type'], c))[0]
            return isNotEmpty(val) && !isNil(val)
        }),
    }
}

export const preparePayload = async (
    blocks: ExtendedBlock[],
    action?: FixMe,
): Promise<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trackers: any[]
    componentValues: BlockPayload[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    containerValues?: any[]
}> => {
    const isCp2 = blocks[0]?.type === 'CONTAINER'
    return isCp2
        ? prepareV2Payload(blocks, action)
        : prepareV1Payload(blocks, action)
}

const waitForAllLayoutDescriptorChunks = async (checks = 0): Promise<boolean> => {
    const { showError, getAppViewMessages } = useWidgetViewManager()
    const { t } = useI18n()

    const messages = uniqBy(
        (m: Message) => path(['layoutDescriptor', 'index'], m),
        getAppViewMessages.value,
    )

    if (checks >= 50 /*(5 seconds)*/) {
        showError({
            title: t('views.widget.incomplete_chunks.title'),
            description: t('views.widget.incomplete_chunks.description'),
            retry: true,
        })

        return false
    }

    if (typeof messages[0]?.layoutDescriptor === 'object') {
        const total = messages[0].layoutDescriptor.total
        const allFetched = messages
            .filter(m => !!path(['layoutDescriptor', 'chunk'], m))
            .length === total

        if (!allFetched) {
            await promiseTimeout(100)
            await waitForAllLayoutDescriptorChunks(checks + 1)
        }

        return true
    }

    return true
}


export const decompressLayoutDescriptor = async (): Promise<CustomPageLayout> => {
    const { getAppViewMessages } = useWidgetViewManager()
    await waitForAllLayoutDescriptorChunks()

    const messages = uniqBy(
        (m: Message) => path(['layoutDescriptor', 'index'], m),
        getAppViewMessages.value,
    )

    const baseDescriptor = messages
        .map(m => m.layoutDescriptor as LayoutDescriptor)
        .sort((a, b) => a.index < b.index ? -1 : 1)
        .reduce((res, ld) => res += ld.chunk, '')
    const gzipped = Uint8Array.from(atob(baseDescriptor), char => char.charCodeAt(0))
    const layoutString = pako.inflate(gzipped, { to: 'string' })
    return JSON.parse(layoutString) as CustomPageLayout
}

export const uploadFiles = async (block: ExtendedBlock) => {
    const { uploadFile } = useApi()
    const websocket = useWebsocket()
    const conduct = websocket.latestMessage.value

    const layoutDescriptor = typeof conduct.layoutDescriptor === 'string'
        ? JSON.parse(conduct.layoutDescriptor)
        : await decompressLayoutDescriptor()

    const customPageId = layoutDescriptor?.id
    const promises: FixMe = []

    if (block.currentValue) {
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        Object.entries(block.currentValue).forEach(([_, value]: FixMe) => {
            if (value) {

                Object.values((value.files || {})).forEach((file: FixMe) => {

                    if (file.resource) {
                        promises.push(Promise.resolve(file.resource))
                        return
                    }

                    if (file.uploadedData) {
                        promises.push(Promise.resolve(file.uploadedData))
                        return
                    }

                    promises.push(uploadFile({
                        blockType: block.type,
                        workflowId: conduct.workflowId,
                        workspaceId: conduct.workspaceId,
                        partyId: conduct.partyId,
                        taskId: conduct.taskId,
                        customPageId,
                        inputDocumentTaskId: block.id,
                        fileName: file.name,
                        file: file.data,
                        fileRef: file,
                    }))
                })
            }
        })
    }


    return Promise.all(promises)
}

export const removeFile = async ({ fileName, block, resourceId }: FixMe) => {
    const websocket = useWebsocket()
    const conduct = websocket.latestMessage.value
    const { removeFile } = useApi()

    const layoutDescriptor = typeof conduct.layoutDescriptor === 'string'
        ? JSON.parse(conduct.layoutDescriptor)
        : await decompressLayoutDescriptor()

    return removeFile({
        workspaceId: conduct.workspaceId,
        partyId: conduct.partyId,
        taskId: conduct.taskId,
        customPageId: layoutDescriptor?.id,
        inputDocumentTaskId: block.id,
        resourceId,
        fileName,
    })
}

export const convertBase64ToBlob = (base: string) => {
    const parts = base.split(';base64,')

    // Hold the content type
    const imageType = parts[0].split(':')[1]

    const decodedData = window.atob(parts[1])
    const uInt8Array = new Uint8Array(decodedData.length)

    for (let i = 0; i < decodedData.length; i++) {
        uInt8Array[i] = decodedData.charCodeAt(i)
    }

    return new Blob([uInt8Array], { type: imageType })
}

export const isGridLayoutProvided = (blocks: ExtendedBlock[]) => {
    return blocks.every(b =>
        b.layout?.x !== undefined &&
        b.layout?.y !== undefined &&
        b.layout?.w !== undefined &&
        b.layout?.h !== undefined,
    )
}
